import { useRef } from "react";
import { animate, createTimeline, stagger, svg } from "animejs";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import profileImage from "../../assets/images/profile.png";
import useAnimeScope from "../../hooks/useAnimeScope";
import AnimatedNumber from "../AnimatedNumber/AnimatedNumber";
import "./Hero.scss";

const heroWords = ["Ahmad", "Rajput"];

const proofPoints = [
  { value: "50+", label: "Workflows shipped" },
  { value: "15+", label: "Client ecosystems" },
  { value: "3+", label: "Years in AI" }
];

const techChips = [
  "n8n",
  "FastAPI",
  "Frappe / ERPNext",
  "RAG",
  "PGVector",
  "AWS Lambda",
  "MCP",
  "Docker",
  "HubSpot"
];

const Hero = () => {
  const rootRef = useRef(null);

  useAnimeScope(rootRef, () => {
    createTimeline({
      defaults: { ease: "outExpo", duration: 780 }
    })
      .add(".hero__profile", {
        opacity: [0, 1],
        scale: [0.72, 1],
        rotate: [-8, 0]
      })
      .add(".hero__eyebrow", {
        opacity: [0, 1],
        translateY: [14, 0]
      }, "-=520")
      .add(".hero__word", {
        opacity: [0, 1],
        translateY: ["110%", "0%"],
        rotateX: [55, 0],
        delay: stagger(80)
      }, "-=520")
      .add(".hero__title", {
        opacity: [0, 1],
        translateY: [16, 0]
      }, "-=500")
      .add(".hero__bio", {
        opacity: [0, 1],
        translateY: [16, 0]
      }, "-=520")
      .add(".hero__cta a", {
        opacity: [0, 1],
        translateY: [12, 0],
        delay: stagger(80)
      }, "-=420")
      .add(".hero__proof", {
        opacity: [0, 1],
        translateY: [14, 0],
        delay: stagger(80)
      }, "-=420")
      .add(".hero__console", {
        opacity: [0, 1],
        translateY: [18, 0]
      }, "-=520")
      .add(".hero__chip", {
        opacity: [0, 1],
        scale: [0.78, 1],
        delay: stagger(45, { from: "center" })
      }, "-=600");

    animate(".hero__ring", {
      rotate: "1turn",
      duration: 9000,
      loop: true,
      ease: "linear"
    });

    animate(".hero__chip", {
      translateY: [
        { to: -6, duration: 1500 },
        { to: 6, duration: 1500 }
      ],
      delay: stagger(120, { from: "random" }),
      alternate: true,
      loop: true,
      ease: "inOutSine"
    });

    animate(svg.createDrawable(".hero__circuit-line"), {
      draw: ["0 0", "0 1", "1 1"],
      duration: 3400,
      delay: stagger(260),
      loop: true,
      ease: "inOutQuad"
    });
  }, []);

  const scrollToSection = (sectionId) => (event) => {
    event.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="hero" ref={rootRef}>
      <div className="hero__grid">
        <div className="hero__copy">
          <p className="hero__eyebrow">Independent AI automation engineer</p>

          <h1 className="hero__name" aria-label="Ahmad Rajput">
            {heroWords.map((word) => (
              <span className="hero__word-wrap" key={word}>
                <span className="hero__word">{word}</span>
              </span>
            ))}
          </h1>

          <h2 className="hero__title">
            I build automation systems that remove busywork from real operations.
          </h2>

          <p className="hero__bio">
            Multi-agent workflows, RAG pipelines, document intelligence, CRM integrations,
            cloud scrapers, and backend systems that ship into client environments.
          </p>

          <div className="hero__cta" aria-label="Main actions">
            <a href="#projects" className="btn-primary" onClick={scrollToSection("projects")}>
              View Work
            </a>
            <a href="#contact" className="btn-glass" onClick={scrollToSection("contact")}>
              Let us Talk
            </a>
          </div>

          <div className="hero__proofs" aria-label="Professional proof points">
            {proofPoints.map((point) => (
              <div className="hero__proof" key={point.label}>
                <AnimatedNumber value={point.value} className="hero__proof-number" />
                <span>{point.label}</span>
              </div>
            ))}
          </div>

          <div className="hero__social" aria-label="Social links">
            <a href="https://github.com/AhmadBinKhalil" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FaGithub />
            </a>
            <a href="https://linkedin.com/in/ahmad-bin-khalil" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FaLinkedin />
            </a>
          </div>
        </div>

        <div className="hero__visual">
          <div className="hero__profile-card">
            <div className="hero__profile">
              <span className="hero__ring" />
              <img
                src={profileImage}
                alt="Ahmad Rajput - AI automation engineer"
                width="172"
                height="172"
                decoding="async"
                fetchPriority="high"
              />
            </div>

            <svg className="hero__circuit" viewBox="0 0 460 360" aria-hidden="true">
              <path className="hero__circuit-line" d="M50 76 C150 30 220 95 320 54 S420 72 440 126" />
              <path className="hero__circuit-line" d="M28 250 C140 180 210 300 318 236 S408 210 444 282" />
              <path className="hero__circuit-line" d="M80 180 C170 150 220 206 300 176 S400 148 442 190" />
            </svg>

            <div className="hero__console">
              <div className="hero__console-top">
                <span />
                <span />
                <span />
              </div>
              <pre>{`agent.route("lead");
rag.query("client_context");
workflow.ship("production");`}</pre>
            </div>
          </div>

          <div className="hero__chips" aria-label="Tech stack highlights">
            {techChips.map((chip) => (
              <span className="hero__chip" key={chip}>
                {chip}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="hero__scroll" aria-hidden="true">
        <div className="scroll-line" />
        <span>scroll</span>
      </div>
    </section>
  );
};

export default Hero;
